import { z } from "zod";
import {
  designerBasisField,
  designerDispatchEnum,
  findingSeverityField,
  findingSource,
  findingTypeEnum,
  regulatoryWeightEnum,
  triageStatusEnum,
} from "../../types/enums.js";
import {
  complianceImpactSchema,
  confidenceSchema,
  fixStatusSchema,
  taskImpactSchema,
} from "../../validation/finding-metrics.js";
import {
  entityBaseSchema,
  generatedBySchema,
  isoTimestamp,
  slugSchema,
} from "./common.js";

/** Why a finding was dismissed. */
export const DISMISS_REASONS = [
  "not_relevant",
  "walker_error",
  "out_of_scope",
  "duplicate",
] as const;
export const dismissReasonSchema = z.enum(DISMISS_REASONS);
export type DismissReason = z.infer<typeof dismissReasonSchema>;

/**
 * The advisor's check of a walker's severity against the code and the
 * screenshots: `confirmed`, `overstated` / `understated` (the finding's
 * `severity` then holds the checked value), or `setup` (it exists only
 * because the run lacked the data the situation needs).
 */
export const SEVERITY_CHECK_VERDICTS = [
  "confirmed",
  "overstated",
  "understated",
  "setup",
] as const;
export const severityCheckSchema = z.object({
  verdict: z.enum(SEVERITY_CHECK_VERDICTS),
  /** The severity the walker filed. */
  filed: findingSeverityField.nullable(),
  /** What the code or the screenshots show. */
  why: z.string(),
});
export type SeverityCheck = z.infer<typeof severityCheckSchema>;

/**
 * Platform-only legacy bounding box. The CLI never sends it — it sends
 * `element_rect`; both land in `findings.target_box`.
 */
export const targetBoxSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  viewport_width: z.number().optional(),
  viewport_height: z.number().optional(),
  viewportWidth: z.number().optional(),
  viewportHeight: z.number().optional(),
});
export type TargetBoxSchema = z.infer<typeof targetBoxSchema>;

/** Prefix of `seen_in` for a designer finding from a run's designer's review. */
export const RUN_REVIEW_PREFIX = "ux:";
/** Prefix of `seen_in` for a designer finding from a page review. */
export const PAGE_REVIEW_PREFIX = "review:";

/**
 * Fields every finding file may carry, whichever eye found it. The two
 * branches below decide what is required.
 */
const findingFieldsSchema = entityBaseSchema.extend({
  /** Slug ref to scenario this finding belongs to. */
  scenario: slugSchema.nullish(),
  /** Slug ref to persona (redundant with scenario, kept for convenience + direct cross-persona aggregation). Whose run it is, for a designer finding. */
  persona: slugSchema.nullish(),
  /** Directory-name of the run that produced this finding, e.g. `2026-04-23T14-23-maria-shops-tv-abc123`. */
  run: z.string().min(1).nullish(),
  type: findingTypeEnum,
  /** Harm to the person. The legacy `observation` reads as `nit`. */
  severity: findingSeverityField.nullish(),
  compliance_impact: complianceImpactSchema.nullish(),
  task_impact: taskImpactSchema.nullish(),
  confidence: confidenceSchema.nullish(),
  regulatory_weight: regulatoryWeightEnum.nullish(),
  triage_status: triageStatusEnum.default("open"),
  triage_note: z.string().nullish(),
  /**
   * Which CLI command set `triage_status` (`triage`, `designer-dismiss`,
   * a sweep, …). Sent only to a cloud declaring `finding-provenance`.
   */
  triaged_by: z.string().max(64).nullish(),
  /**
   * When the fix was applied: stamped once on the open → addressed
   * transition and never rewritten by a re-triage (a note, a sweep), so
   * the fix clock that `verified` is measured against stays put. Absent
   * on older files, where `updated_at` stands in.
   */
  addressed_at: isoTimestamp.nullish(),
  /**
   * Why a `dismissed` finding was dismissed: `not_relevant` (by design),
   * `walker_error` (the walker misread the product), `out_of_scope`
   * (handled outside the system under test), or `duplicate`. Absent
   * means `not_relevant`. The first three also create a tester guidance
   * rule.
   */
  dismiss_reason: dismissReasonSchema.nullish(),
  /** Finding slug or id this repeats when `dismiss_reason` is `duplicate`. */
  duplicate_of: z.string().nullish(),
  /**
   * The active tester guidance rule this finding matches, set by
   * `iamu match-record`. The finding is kept — the rule flagged it,
   * it did not drop it.
   */
  suppressed_by_guidance: z.string().uuid().nullish(),
  fix_status: fixStatusSchema.default("open"),
  /**
   * LEGACY: who found it, before `source`. A file without `source` and
   * with `lens: designer` reads as a designer finding.
   */
  lens: z.string().default("persona"),
  /** @deprecated Read, never written by current CLIs. */
  ux_heuristic: z.string().nullish(),
  /** @deprecated Read, never written; a design cause uses `design_cause_of`. */
  corroborates: z.array(z.string()).default([]),
  /**
   * Designer findings: who wrote the review, `designer@<version>`. The
   * entity-wide prompt audit object is still accepted.
   */
  generated_by: z.union([z.string(), generatedBySchema]).nullish(),
  /** Designer findings: how strong the claim is (`observed` reads as `seen`). */
  basis: designerBasisField.nullish(),
  /** Designer findings: what it is about (hierarchy, copy, states, …). */
  category: z.string().nullish(),
  /** Designer findings: the screen (URL path or name) it is on. */
  screen: z.string().nullish(),
  /** Designer findings: the step numbers it was seen at. */
  steps: z.array(z.number().int()).nullish(),
  /** Designer findings: the persona finding ids it cites. */
  evidence: z.array(z.string()).nullish(),
  /** Designer findings: what is wrong, specifically. */
  what: z.string().nullish(),
  /** Designer findings: why it matters to the person. */
  why: z.string().nullish(),
  /** Designer findings: the design-doc rule it breaks, by name. Never raises severity. */
  breaks: z.string().nullish(),
  /** Designer findings: the design-doc entry that rule is in. */
  breaks_file: z.string().nullish(),
  /**
   * Designer findings: the persona finding (id) this is the design cause
   * of. It then belongs to that finding's canonical and has none itself.
   */
  design_cause_of: z.string().uuid().nullish(),
  /** Designer findings: which review saw it, `ux:<run-id>` or `review:<surface>`. */
  seen_in: z.string().nullish(),
  /** Designer findings: `inline` when the orchestrator wrote the review itself. */
  dispatch: designerDispatchEnum.nullish(),
  /** If finding maps to a specific human-test question on the outline, its id. */
  human_test_id: z.string().nullish(),
  /** The run whose judge set `human_test_id` (`iamu evaluate-record`). */
  human_test_set_by: z.string().max(128).nullish(),
  /** The id an older or hand-written file carried before `iamu repair`. */
  legacy_id: z.string().max(128).nullish(),
  /** Short title for list rendering. Full text goes in the body. */
  title: z.string().nullish(),
  /** What the UI showed or did — observable without domain knowledge. Depth-gate half 1. */
  ui_fact: z.string().nullish(),
  /** What the UI fact means for the persona completing the task. Depth-gate half 2. */
  consequence: z.string().nullish(),
  /**
   * What the persona needed at that moment, in their terms, with no
   * solution (set by the walker through `new-finding --need`). The
   * advisor writes the fix from it.
   */
  need: z.string().nullish(),
  /**
   * Who wrote the recommendation after the walk: `advisor@<version>`,
   * set by `iamu advise-record`. Absent means the recommendation (if
   * any) came from the walker or a person.
   */
  advised_by: z
    .string()
    .regex(/^advisor@\S+$/, "advised_by must look like advisor@<version>")
    .nullish(),
  /** The advisor's check of `severity` (set by `iamu advise-record`). */
  severity_check: severityCheckSchema.nullish(),
  /**
   * The step whose screenshot shows the finding — `step-N` (set by
   * `iamu new-finding --step N`). The finding's evidence in the report,
   * the cloud and the dashboard.
   */
  walkthrough_step_id: z.string().nullish(),
  /** Platform-only legacy box; see {@link targetBoxSchema}. */
  target_box: targetBoxSchema.nullish(),
  /** Citations to sources/sections that back the finding. Free-form strings per current cloud shape. */
  citations: z.array(z.string()).default([]),
  /** Stable ID linking this finding to the same issue across runs. Computed from scenario + type + ui_fact fingerprint. */
  canonical_id: z.string().nullish(),
  /** Optional bounding box marking on the step screenshot (coordinates x, y, width, height). */
  element_rect: z
    .object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .nullish(),
  /** Optional element selector or label. */
  element_selector: z.string().nullish(),
  /** Explicit URL / route of the page where the finding was observed. */
  page_url: z.string().nullish(),
  /** Explicit screen title / action label where the finding was observed. */
  screen_title: z.string().nullish(),
});

const required = (what: string) =>
  z
    .string()
    .trim()
    .min(1, { message: `${what} is required` });

/**
 * A persona finding: what a persona experienced on a walk. Unchanged
 * from before `source` existed: its scenario, persona and run are
 * required.
 */
export const personaFindingFrontmatterSchema = findingFieldsSchema.extend({
  source: z.literal("persona"),
  scenario: slugSchema,
  persona: slugSchema,
  run: z.string().min(1),
});

/**
 * A designer finding: what the designer saw reviewing a run's screens
 * (`seen_in: ux:<run>`) or a page (`seen_in: review:<surface>`). Needs
 * `what`, `why`, `basis` and `screen`; not held to the persona depth
 * gate. One from a run also names its scenario, persona and run.
 */
export const designerFindingFrontmatterSchema = findingFieldsSchema
  .extend({
    source: z.literal("designer"),
    what: required("what (what the designer saw, specifically)"),
    why: required("why (why it matters to the person)"),
    basis: designerBasisField,
    screen: required("screen (the screen it is on)"),
  })
  .superRefine((fm, ctx) => {
    if (isPageReviewFinding(fm)) return;
    for (const field of ["scenario", "persona", "run"] as const) {
      if (!fm[field]) {
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: `${field} is required for a designer finding from a run (seen_in ${RUN_REVIEW_PREFIX}…)`,
        });
      }
    }
  });

/**
 * Finding frontmatter, discriminated by `source`. A file without
 * `source` reads as `persona`, unless it carries the legacy
 * `lens: designer`.
 *
 * Mirrors `findings` table in cloud schema.
 * Body markdown holds the full description + recommendation.
 */
export const findingFrontmatterSchema = z.preprocess(
  (value) =>
    value && typeof value === "object" && !Array.isArray(value)
      ? {
          ...value,
          source: findingSource(
            value as { source?: string | null; lens?: string | null },
          ),
        }
      : value,
  z.discriminatedUnion("source", [
    personaFindingFrontmatterSchema,
    designerFindingFrontmatterSchema,
  ]),
);

export type FindingFrontmatter = z.infer<typeof findingFrontmatterSchema>;
export type PersonaFindingFrontmatter = z.infer<
  typeof personaFindingFrontmatterSchema
>;
export type DesignerFindingFrontmatter = z.infer<
  typeof designerFindingFrontmatterSchema
>;

/** A designer finding from a page review: no run, scenario or persona. */
export function isPageReviewFinding(fm: { seen_in?: string | null }): boolean {
  return String(fm.seen_in ?? "").startsWith(PAGE_REVIEW_PREFIX);
}

/** A designer finding from a run's designer's review (`seen_in: ux:…`). */
export function isRunReviewFinding(fm: { seen_in?: string | null }): boolean {
  return String(fm.seen_in ?? "").startsWith(RUN_REVIEW_PREFIX);
}

/** `generated_by` as stored on `findings.generated_by` (a string). */
export function generatedByLabel(
  value: FindingFrontmatter["generated_by"],
): string | null {
  if (!value) return null;
  return typeof value === "string" ? value : `${value.id}@${value.version}`;
}
